/*
 * Interaction patterns adapted from BeUI source components (MIT License).
 * https://beui.dev/ — Copyright BeUI contributors.
 * Yi keeps only the interaction logic and uses its own semantic styles.
 */
import {
  AnimatePresence,
  motion,
  useDragControls,
  useReducedMotion,
  type HTMLMotionProps,
  type Transition,
} from "motion/react";
import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEventHandler,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from "react";
import { useLocation } from "react-router-dom";

export type ButtonState = "idle" | "loading" | "success" | "error";
export type ButtonVariant = "primary" | "secondary" | "quiet" | "danger";

export const restrainedSpring: Transition = {
  type: "spring",
  stiffness: 420,
  damping: 34,
  mass: 0.75,
};

const stateMarks: Record<Exclude<ButtonState, "idle">, string> = {
  loading: "…",
  success: "✓",
  error: "!",
};

export interface MotionButtonProps extends Omit<HTMLMotionProps<"button">, "children"> {
  children: ReactNode;
  variant?: ButtonVariant;
  state?: ButtonState;
  stateLabels?: Partial<Record<ButtonState, string>>;
}

export function MotionButton({
  children,
  className = "",
  variant = "primary",
  state = "idle",
  stateLabels,
  disabled,
  ...props
}: MotionButtonProps) {
  const reduceMotion = useReducedMotion();
  const visibleLabel = stateLabels?.[state] ?? children;
  const active = state === "idle" || state === "error";

  return <motion.button
    {...props}
    className={`button ${variant} motion-button state-${state} ${className}`.trim()}
    disabled={disabled || state === "loading"}
    aria-busy={state === "loading" || undefined}
    data-state={state}
    whileHover={active && !disabled && !reduceMotion ? { y: -2 } : undefined}
    whileTap={active && !disabled && !reduceMotion ? { scale: 0.97 } : undefined}
    transition={reduceMotion ? { duration: 0 } : restrainedSpring}
  >
    <AnimatePresence mode="wait" initial={false}>
      <motion.span
        className="motion-button-content"
        key={state}
        initial={reduceMotion ? false : { opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={reduceMotion ? undefined : { opacity: 0, y: -4 }}
        transition={{ duration: reduceMotion ? 0 : 0.16 }}
      >
        {state !== "idle" && <span className="button-state-mark" aria-hidden="true">{stateMarks[state]}</span>}
        <span>{visibleLabel}</span>
      </motion.span>
    </AnimatePresence>
  </motion.button>;
}

export function StatefulButton(props: MotionButtonProps) {
  return <MotionButton {...props} />;
}

type SharedFieldProps = {
  as?: "input" | "textarea";
  label?: ReactNode;
  error?: string;
  success?: boolean;
  reserveErrorLine?: boolean;
  fieldClassName?: string;
  after?: ReactNode;
};

type MotionInputProps = SharedFieldProps & Omit<InputHTMLAttributes<HTMLInputElement>, "as" | "children"> & {
  as?: "input";
  onChange?: ChangeEventHandler<HTMLInputElement>;
};

type MotionTextareaProps = SharedFieldProps & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "as" | "children"> & {
  as: "textarea";
  onChange?: ChangeEventHandler<HTMLTextAreaElement>;
};

export type MotionFieldProps = MotionInputProps | MotionTextareaProps;

export const MotionField = forwardRef<HTMLInputElement | HTMLTextAreaElement, MotionFieldProps>(function MotionField(
  { as = "input", label, error, success = false, reserveErrorLine = true, fieldClassName = "", className = "", after, id: providedId, ...attributes },
  ref,
) {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  const errorId = `${id}-error`;
  const reduceMotion = useReducedMotion();
  const hadError = useRef(false);
  const [shake, setShake] = useState(0);

  useEffect(() => {
    if (error && !hadError.current) setShake((value) => value + 1);
    hadError.current = Boolean(error);
  }, [error]);

  const shared = {
    id,
    className: `motion-field-control ${className}`.trim(),
    "aria-invalid": Boolean(error) || undefined,
    "aria-describedby": error || reserveErrorLine ? errorId : undefined,
  };

  return <motion.label
    className={`field motion-field ${success ? "is-success" : ""} ${fieldClassName}`.trim()}
    animate={!reduceMotion && shake ? { x: [0, -6, 5, -3, 0] } : { x: 0 }}
    transition={{ duration: reduceMotion ? 0 : 0.24 }}
  >
    {label && <span>{label}</span>}
    <span className="motion-field-frame">
      {as === "textarea"
        ? <textarea {...(attributes as TextareaHTMLAttributes<HTMLTextAreaElement>)} {...shared} ref={ref as React.Ref<HTMLTextAreaElement>} />
        : <input {...(attributes as InputHTMLAttributes<HTMLInputElement>)} {...shared} ref={ref as React.Ref<HTMLInputElement>} />}
      {success && !error && <motion.span className="field-success" aria-hidden="true" initial={reduceMotion ? false : { scale: 0 }} animate={{ scale: 1 }} transition={restrainedSpring}>✓</motion.span>}
      {after}
    </span>
    {(reserveErrorLine || error) && <span className="motion-field-message" id={errorId} role={error ? "alert" : undefined}>{error ?? "\u00a0"}</span>}
  </motion.label>;
});

export interface MotionCheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  children: ReactNode;
  fieldClassName?: string;
}

export function MotionCheckbox({ children, className = "", fieldClassName = "", checked, ...props }: MotionCheckboxProps) {
  const reduceMotion = useReducedMotion();
  return <label className={`check-row motion-checkbox ${fieldClassName}`.trim()}>
    <input {...props} className={`sr-only ${className}`.trim()} type="checkbox" checked={checked} />
    <span className="motion-checkbox-box" aria-hidden="true">
      <svg viewBox="0 0 18 18" fill="none">
        <motion.path
          d="m4 9.2 3.1 3.1L14 5.8"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={false}
          animate={{ pathLength: checked ? 1 : 0, opacity: checked ? 1 : 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.2 }}
        />
      </svg>
    </span>
    <span>{children}</span>
  </label>;
}

export function TextReveal({ children, delay = 0, className = "" }: { children: ReactNode; delay?: number; className?: string }) {
  const reduceMotion = useReducedMotion();
  return <motion.span
    className={className}
    initial={reduceMotion ? false : { y: 10 }}
    animate={{ y: 0 }}
    transition={{ duration: reduceMotion ? 0 : 0.24, delay: reduceMotion ? 0 : delay, ease: [0.16, 1, 0.3, 1] }}
  >{children}</motion.span>;
}

export function TextShimmer({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <span className={`text-shimmer ${className}`.trim()}>{children}</span>;
}

export interface BottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  snapPoints?: number[];
  id?: string;
  children: ReactNode;
  closeLabel: string;
}

const focusableSelector = 'a[href], button:not([disabled]), select:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function BottomSheet({ open, onOpenChange, title, description, snapPoints = [0.82], id = "bottom-sheet", children, closeLabel }: BottomSheetProps) {
  const reduceMotion = useReducedMotion();
  const location = useLocation();
  const dialog = useRef<HTMLDivElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const trigger = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descriptionId = useId();
  const dragControls = useDragControls();
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  const effectiveSnapPoints = (snapPoints.length ? snapPoints : [0.82])
    .map((point) => Math.max(0.25, Math.min(point, 0.96)))
    .sort((left, right) => left - right);
  const minSnap = effectiveSnapPoints[0]!;
  const maxSnap = effectiveSnapPoints[effectiveSnapPoints.length - 1]!;
  const [activeSnap, setActiveSnap] = useState(maxSnap);

  useEffect(() => {
    if (open) setActiveSnap(maxSnap);
  }, [maxSnap, open]);

  useEffect(() => {
    if (!open) return;
    trigger.current = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButton.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onOpenChangeRef.current(false);
        return;
      }
      if (event.key !== "Tab" || !dialog.current) return;
      const focusable = [...dialog.current.querySelectorAll<HTMLElement>(focusableSelector)];
      if (!focusable.length) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      trigger.current?.focus();
    };
  }, [open]);

  const pathname = location.pathname;
  const openedPath = useRef(pathname);
  useEffect(() => {
    if (open) openedPath.current = pathname;
  }, [open]);
  useEffect(() => {
    if (open && pathname !== openedPath.current) onOpenChangeRef.current(false);
  }, [open, pathname]);

  return <AnimatePresence>
    {open && <motion.div
      className="sheet-layer"
      role="presentation"
      initial={reduceMotion ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: reduceMotion ? 0 : 0.18 }}
      onPointerDown={(event) => {
        if (event.target !== event.currentTarget) return;
        event.preventDefault();
        onOpenChange(false);
      }}
    >
      <motion.div
        id={id}
        className="more-sheet"
        data-snap-point={activeSnap}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        ref={dialog}
        style={{ "--sheet-max-height": `${maxSnap * 100}dvh` } as React.CSSProperties}
        initial={reduceMotion ? false : { y: "100%" }}
        animate={{ y: `${(maxSnap - activeSnap) * 100}dvh` }}
        exit={{ y: "100%" }}
        transition={reduceMotion ? { duration: 0 } : restrainedSpring}
        drag={reduceMotion ? false : "y"}
        dragControls={dragControls}
        dragListener={false}
        dragConstraints={{ top: 0, bottom: 1000 }}
        dragElastic={{ top: 0.08, bottom: 0.08 }}
        onDragEnd={(_, info) => {
          const projectedSnap = activeSnap - (info.offset.y / window.innerHeight);
          if (info.velocity.y > 650 || projectedSnap < minSnap * 0.45) {
            onOpenChange(false);
            return;
          }
          const nearest = effectiveSnapPoints.reduce((best, point) => Math.abs(point - projectedSnap) < Math.abs(best - projectedSnap) ? point : best);
          setActiveSnap(nearest);
        }}
      >
        <div
          className="sheet-handle"
          aria-hidden="true"
          onClick={() => setActiveSnap((current) => current === maxSnap ? minSnap : maxSnap)}
          onPointerDown={(event) => { if (!reduceMotion) dragControls.start(event); }}
        />
        <header>
          <div><p className="eyebrow">Yi · 易</p><h2 id={titleId}>{title}</h2>{description && <p id={descriptionId}>{description}</p>}</div>
          <button ref={closeButton} type="button" className="icon-button" onClick={() => onOpenChange(false)} aria-label={closeLabel}><span aria-hidden="true">×</span></button>
        </header>
        {children}
      </motion.div>
    </motion.div>}
  </AnimatePresence>;
}

export function SharedActiveIndicator({ id }: { id: string }) {
  const reduceMotion = useReducedMotion();
  return <motion.span
    className="shared-active-indicator"
    aria-hidden="true"
    layoutId={`active-indicator-${id}`}
    transition={reduceMotion ? { duration: 0 } : restrainedSpring}
  />;
}
